'use client';

import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useMemo, useState } from 'react';
import { type Address, formatUnits, isAddressEqual } from 'viem';
import { useWalletClient } from 'wagmi';
import type { CheckoutEntryPoint } from '@/lib/analytics/client/events';
import VaultV1Recap from '@/components/Bundler/ConfirmDialog/Recaps/VaultV1Recap';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useVaultV1SimulatedFinalPosition } from '@/components/VaultPage/VaultV1/hooks/useVaultV1SimulatedFinalPosition';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getRequirementsErrorMessage } from '@/helpers/requirementErrors';
import { getWalletProvider } from '@/helpers/walletProvider';
import { buildReviewDialogParams, type CheckoutAttemptTrackingContext } from '@/lib/analytics/client/trackers/checkout-funnel';
import { trackVaultDepositCompleted } from '@/lib/analytics/client/trackers/vault-deposit';
import { getSdkTracking } from '@/lib/observability/client/trackers/transaction/sdk';
import { logger } from '@/lib/observability/logger';
import { useLogSdkBuildFailed } from '@/lib/observability/sdk';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import { InterfaceOperation } from '@/operations/operations.types';
import { tryBuildSimulationAuthorizations } from '@/services/simulate/simulatedPosition.helpers';
import { useVaultV1DepositTransaction } from './useVaultV1DepositTransaction';
import VaultV1OperationReviewDialog from './VaultV1OperationReviewDialog';
interface VaultV1DepositReviewDialogProps {
  vaultAddress: Address;
  inputAmount: bigint;
  erc20Amount: bigint;
  nativeAmount: bigint;
  vaultAsset: InterfaceToken;
  userPosition: bigint;
  finalPosition: bigint | undefined;
  onTxSuccess: () => void;
  entryPoint: CheckoutEntryPoint;
}
const VaultV1DepositReviewDialog = t0 => {
  const $ = _c(77);
  const {
    vaultAddress,
    inputAmount,
    erc20Amount,
    nativeAmount,
    vaultAsset,
    userPosition,
    finalPosition,
    onTxSuccess,
    entryPoint
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    data: client
  } = useWalletClient();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    targetChain,
    account
  } = useWeb3Context();
  const [reviewSessionId] = useState(_temp);
  let t1;
  if ($[0] !== erc20Amount || $[1] !== nativeAmount || $[2] !== vaultAddress || $[3] !== vaultAsset.address) {
    t1 = {
      vaultAddress,
      vaultAsset: vaultAsset.address,
      erc20Amount,
      nativeAmount
    };
    $[0] = erc20Amount;
    $[1] = nativeAmount;
    $[2] = vaultAddress;
    $[3] = vaultAsset.address;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    transaction,
    transactionUserAddress,
    requirements,
    hasRequirementsError,
    hasPlaceholderData,
    hasTransactionError,
    hasVaultDataError,
    preparedError,
    isLoading
  } = useVaultV1DepositTransaction(t1);
  let t2;
  if ($[5] !== account.address || $[6] !== requirements || $[7] !== targetChain.id) {
    t2 = tryBuildSimulationAuthorizations({
      requirements,
      chainId: targetChain.id,
      from: account.address
    });
    $[5] = account.address;
    $[6] = requirements;
    $[7] = targetChain.id;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    simulationAuthorizations,
    simulationApprovalError
  } = t2;
  const t3 = preparedError ?? simulationApprovalError;
  let t4;
  if ($[9] !== reviewSessionId || $[10] !== t3 || $[11] !== targetChain.id) {
    t4 = {
      sdkAction: "deposit",
      chainId: targetChain.id,
      reviewSessionId,
      error: t3
    };
    $[9] = reviewSessionId;
    $[10] = t3;
    $[11] = targetChain.id;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useLogSdkBuildFailed(t4);
  let t5;
  if ($[13] !== erc20Amount) {
    t5 = erc20Amount.toString();
    $[13] = erc20Amount;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== nativeAmount) {
    t6 = nativeAmount.toString();
    $[15] = nativeAmount;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = ["deposit", t5, t6];
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const simulationKey = t7;
  let t8;
  if ($[20] !== account.address || $[21] !== transactionUserAddress) {
    t8 = !!transactionUserAddress && !!account.address && !isAddressEqual(transactionUserAddress, account.address);
    $[20] = account.address;
    $[21] = transactionUserAddress;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const isTransactionStale = t8;
  let t9;
  if ($[23] !== finalPosition || $[24] !== isLoading || $[25] !== simulationAuthorizations || $[26] !== simulationKey || $[27] !== transaction || $[28] !== vaultAddress || $[29] !== vaultAsset.chain.id) {
    t9 = {
      vaultAddress,
      chainId: vaultAsset.chain.id,
      transaction,
      simulationAuthorizations,
      simulationKey,
      fallbackPosition: finalPosition,
      isPreparing: isLoading
    };
    $[23] = finalPosition;
    $[24] = isLoading;
    $[25] = simulationAuthorizations;
    $[26] = simulationKey;
    $[27] = transaction;
    $[28] = vaultAddress;
    $[29] = vaultAsset.chain.id;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const {
    displayedFinalPosition,
    isUnverifiedFinalPosition,
    status: simulationStatus,
    isRefreshing: isSimulationRefreshing
  } = useVaultV1SimulatedFinalPosition(t9);
  let t10;
  if ($[31] !== account.isSafe || $[32] !== entryPoint || $[33] !== requirements || $[34] !== reviewSessionId) {
    t10 = buildReviewDialogParams({
      reviewSessionId,
      entryPoint,
      operationTypes: [InterfaceOperation.InterfaceOperationType.metaMorphoDeposit],
      isSafe: account.isSafe,
      requirements
    });
    $[31] = account.isSafe;
    $[32] = entryPoint;
    $[33] = requirements;
    $[34] = reviewSessionId;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const reviewTrackingParams = t10;
  let t11;
  if ($[36] !== account || $[37] !== client || $[38] !== displayedFinalPosition || $[39] !== hasPlaceholderData || $[40] !== hasRequirementsError || $[41] !== hasTransactionError || $[42] !== hasVaultDataError || $[43] !== inputAmount || $[44] !== isTransactionStale || $[45] !== isUnverifiedFinalPosition || $[46] !== onTxSuccess || $[47] !== requirements || $[48] !== simulationApprovalError || $[49] !== simulationStatus || $[50] !== submitTransactions || $[51] !== targetChain || $[52] !== toggleDialog || $[53] !== transaction || $[54] !== userPosition || $[55] !== vaultAddress || $[56] !== vaultAsset) {
    t11 = checkoutTracking => {
      if (!transaction || !client || !account.isWalletTypeResolved || account.isWrongChain || hasRequirementsError || hasPlaceholderData || hasTransactionError || hasVaultDataError || isTransactionStale || simulationStatus === "blacklist" || simulationApprovalError !== undefined) {
        return;
      }
      submitTransactions(client, {
        actionType: InterfaceOperation.InterfaceOperationType.metaMorphoDeposit,
        flow: "vaultV1Deposit",
        transaction,
        ...getSdkTracking(transaction),
        requirements,
        onTxSuccess: () => {
          trackVaultDepositCompleted({
            vaultAddress,
            vaultVersion: "v1",
            assetSymbol: vaultAsset.symbol ?? "UNKNOWN",
            amountRaw: inputAmount.toString(),
            amountUsd: Number(formatUnits(vaultAsset.toUsd(inputAmount) ?? ZERO, 18)),
            chainId: targetChain.id,
            chainName: targetChain.name,
            walletType: getWalletType(account.isSafe, account.isContract),
            walletProvider: getWalletProvider(account.connector)
          });
          onTxSuccess();
        },
        chain: targetChain,
        account,
        checkoutTracking
      }).catch(error => {
        logger.error(error instanceof Error ? error : new Error(String(error)), {
          tags: {
            component: "VaultV1DepositReviewDialog"
          }
        });
        toggleDialog(false);
      });
      toggleDialog(<ConfirmDialogV2 txMessage="Vault Deposit" token={vaultAsset} operationContent={<VaultV1Recap type="deposit" amount={inputAmount} initialPosition={userPosition} finalPosition={displayedFinalPosition} vaultAddress={vaultAddress} isSimulationFallback={isUnverifiedFinalPosition} />} />, {
        closable: false
      });
    };
    $[36] = account;
    $[37] = client;
    $[38] = displayedFinalPosition;
    $[39] = hasPlaceholderData;
    $[40] = hasRequirementsError;
    $[41] = hasTransactionError;
    $[42] = hasVaultDataError;
    $[43] = inputAmount;
    $[44] = isTransactionStale;
    $[45] = isUnverifiedFinalPosition;
    $[46] = onTxSuccess;
    $[47] = requirements;
    $[48] = simulationApprovalError;
    $[49] = simulationStatus;
    $[50] = submitTransactions;
    $[51] = targetChain;
    $[52] = toggleDialog;
    $[53] = transaction;
    $[54] = userPosition;
    $[55] = vaultAddress;
    $[56] = vaultAsset;
    $[57] = t11;
  } else {
    t11 = $[57];
  }
  const handleContinue = t11;
  const t12 = !transaction || !client || !account.isWalletTypeResolved || account.isWrongChain || hasRequirementsError || hasTransactionError || hasVaultDataError || isTransactionStale || simulationApprovalError !== undefined;
  const t13 = hasPlaceholderData || isSimulationRefreshing;
  let t14;
  if ($[58] !== hasRequirementsError || $[59] !== hasTransactionError || $[60] !== hasVaultDataError || $[61] !== isTransactionStale || $[62] !== simulationApprovalError) {
    t14 = simulationApprovalError ? getRequirementsErrorMessage(simulationApprovalError) : hasTransactionError || hasRequirementsError || hasVaultDataError || isTransactionStale ? "This transaction can't be prepared right now. Please try again." : undefined;
    $[58] = hasRequirementsError;
    $[59] = hasTransactionError;
    $[60] = hasVaultDataError;
    $[61] = isTransactionStale;
    $[62] = simulationApprovalError;
    $[63] = t14;
  } else {
    t14 = $[63];
  }
  let t15;
  if ($[64] !== displayedFinalPosition || $[65] !== handleContinue || $[66] !== inputAmount || $[67] !== isLoading || $[68] !== isUnverifiedFinalPosition || $[69] !== reviewTrackingParams || $[70] !== simulationStatus || $[71] !== t12 || $[72] !== t13 || $[73] !== t14 || $[74] !== userPosition || $[75] !== vaultAddress) {
    t15 = <VaultV1OperationReviewDialog type="deposit" amount={inputAmount} initialPosition={userPosition} finalPosition={displayedFinalPosition} vaultAddress={vaultAddress} isButtonDisabled={t12} isLoading={isLoading} isRefreshing={t13} handleContinue={handleContinue} reviewTrackingParams={reviewTrackingParams} simulationStatus={simulationStatus} isUnverifiedFinalPosition={isUnverifiedFinalPosition} errorReason={t14} />;
    $[64] = displayedFinalPosition;
    $[65] = handleContinue;
    $[66] = inputAmount;
    $[67] = isLoading;
    $[68] = isUnverifiedFinalPosition;
    $[69] = reviewTrackingParams;
    $[70] = simulationStatus;
    $[71] = t12;
    $[72] = t13;
    $[73] = t14;
    $[74] = userPosition;
    $[75] = vaultAddress;
    $[76] = t15;
  } else {
    t15 = $[76];
  }
  return t15;
};
export default VaultV1DepositReviewDialog;
function _temp() {
  return crypto.randomUUID();
}